import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './index.css';

// Pages
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import SinistresPage from './pages/SinistresPage';
import SinistreForm from './pages/SinistreForm';
import FastSinistreForm from './pages/FastSinistreForm';
import SinistreDetail from './pages/SinistreDetail';
import RapportsPage from './pages/RapportsPage';
import RapportForm from './pages/RapportForm';
import HonorairesPage from './pages/HonorairesPage';
import HonoraireForm from './pages/HonoraireForm';
import VehiculesPage from './pages/VehiculesPage';
import AssurancesPage from './pages/AssurancesPage';
import ReportingPage from './pages/ReportingPage';
import ParametresPage from './pages/ParametresPage';
import CataloguesPage from './pages/CataloguesPage';
import DocumentVerifyPage from './pages/DocumentVerifyPage';
import ForcePasswordChange from './pages/ForcePasswordChange';
import Verify2FA from './pages/Verify2FA';

import NotFoundPage from './pages/NotFoundPage';
import AgentChat from './components/AgentChat';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--primary-900)' }}>
      <div>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚗</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>HabibExpertiseSoft</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: 'var(--gold-400)' }} />
        </div>
      </div>
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/" replace /> : children;
};

const RoleRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--surface-1)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ color: 'var(--text-primary)' }}>Accès non autorisé</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: '8px' }}>Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
          <button
            className="btn btn-primary"
            style={{ marginTop: '20px' }}
            onClick={() => window.location.href = '/sinistres'}
          >
            Retour aux Dossiers
          </button>
        </div>
      </div>
    );
  }
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/verify-2fa" element={<PublicRoute><Verify2FA /></PublicRoute>} />
        <Route path="/reset-password" element={<PublicRoute><ForcePasswordChange /></PublicRoute>} />
        <Route path="/verify-document/:id" element={<DocumentVerifyPage />} />
        <Route path="/verify/:id" element={<DocumentVerifyPage />} />

        {/* Protected */}
        <Route path="/" element={
          <PrivateRoute>
            {user?.role === 'agent' ? <Navigate to="/sinistres" replace /> : <Dashboard />}
          </PrivateRoute>
        } />

        {/* Sinistrés */}
        <Route path="/sinistres/nouveau" element={<PrivateRoute><SinistreForm /></PrivateRoute>} />
        <Route path="/sinistres/rapide" element={<PrivateRoute><FastSinistreForm /></PrivateRoute>} />
        <Route path="/sinistres/:id/modifier" element={<PrivateRoute><SinistreForm /></PrivateRoute>} />
        <Route path="/sinistres/:id" element={<PrivateRoute><SinistreDetail /></PrivateRoute>} />
        <Route path="/sinistres/edit/:id" element={<PrivateRoute><SinistreForm /></PrivateRoute>} />
        <Route path="/sinistres/*" element={<PrivateRoute><SinistresPage /></PrivateRoute>} />

        {/* Rapports */}
        <Route path="/rapports/*" element={<PrivateRoute><RapportsPage /></PrivateRoute>} />
        <Route path="/rapports/nouveau" element={<PrivateRoute><RapportForm /></PrivateRoute>} />
        <Route path="/rapports/:id/modifier" element={<PrivateRoute><RapportForm /></PrivateRoute>} />
        <Route path="/rapports/:id" element={<PrivateRoute><RapportForm /></PrivateRoute>} />

        {/* Honoraires - Restricted for Agent */}
        <Route path="/honoraires/nouvelle" element={
          <RoleRoute roles={['admin', 'expert']}><HonoraireForm /></RoleRoute>
        } />
        <Route path="/honoraires/:id/modifier" element={
          <RoleRoute roles={['admin', 'expert']}><HonoraireForm /></RoleRoute>
        } />
        <Route path="/honoraires/:id" element={
          <RoleRoute roles={['admin', 'expert']}><HonoraireForm /></RoleRoute>
        } />
        <Route path="/honoraires/*" element={
          <RoleRoute roles={['admin', 'expert']}><HonorairesPage /></RoleRoute>
        } />

        {/* Références */}
        <Route path="/vehicules" element={<PrivateRoute><VehiculesPage /></PrivateRoute>} />
        <Route path="/assurances" element={<PrivateRoute><AssurancesPage /></PrivateRoute>} />
        <Route path="/catalogues" element={<PrivateRoute><CataloguesPage /></PrivateRoute>} />

        {/* Reporting & Admin - Restricted for Agent */}
        <Route path="/reporting" element={
          <RoleRoute roles={['admin', 'expert']}><ReportingPage /></RoleRoute>
        } />
        <Route path="/parametres" element={
          <RoleRoute roles={['admin']}><ParametresPage /></RoleRoute>
        } />

        {/* /audit was a duplicate of /parametres — redirect cleanly */}
        <Route path="/audit" element={<Navigate to="/parametres" replace />} />

        {/* Fallback — proper 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* ── HABIB AI Agent: visible only when logged in ── */}
      {user && <AgentChat />}
    </>
  );
};


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '500',
              boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            },
            success: { style: { background: '#f0fdf4', color: '#065f46', border: '1px solid #bbf7d0' } },
            error: { style: { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' } },
            loading: { style: { background: 'var(--primary-50)', color: 'var(--primary-700)', border: '1px solid var(--primary-100)' } },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
